/**
 * ArtifactLinkCapture – Inline form for pasting external URLs as artifacts.
 */

import { useState } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCaptureLinkArtifact } from '@/hooks/useArtifacts';
import type { LinkEntityType, LinkRole } from '@/lib/artifacts/constants';
import { toast } from 'sonner';

interface ArtifactLinkCaptureProps {
  shopId: string;
  entityType: LinkEntityType;
  entityId: string;
  role: LinkRole;
  className?: string;
}

export function ArtifactLinkCapture({
  shopId,
  entityType,
  entityId,
  role,
  className,
}: ArtifactLinkCaptureProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const captureMutation = useCaptureLinkArtifact();

  const handleSubmit = async () => {
    if (!url.trim()) return;
    try {
      await captureMutation.mutateAsync({
        shopId,
        externalUrl: url.trim(),
        title: title.trim() || undefined,
        link: { entityType, entityId, role },
      });
      toast.success('Link attached');
      setUrl('');
      setTitle('');
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to capture link');
    }
  };

  if (!open) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className={className}
      >
        <Link2 className="h-3.5 w-3.5 mr-1" />
        Link
      </Button>
    );
  }

  return (
    <div className={`flex items-center gap-1.5 ${className || ''}`}>
      <Input
        placeholder="Paste URL…"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        className="h-7 text-xs w-44"
        autoFocus
      />
      <Input
        placeholder="Title (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="h-7 text-xs w-28"
      />
      <Button
        size="sm"
        variant="secondary"
        className="h-7 text-xs"
        onClick={handleSubmit}
        disabled={captureMutation.isPending}
      >
        {captureMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add'}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 text-xs"
        onClick={() => { setOpen(false); setUrl(''); setTitle(''); }}
      >
        Cancel
      </Button>
    </div>
  );
}
